import { supabase } from '../lib/supabase';

export interface Favorite {
  id: string;
  product_id: string;
  user_id: string;
  created_at: string;
}

export class FavoritesService {
  // Add a product to favorites
  static async addToFavorites(productId: string, userId: string): Promise<Favorite> {
    try {
      const { data, error } = await supabase
        .from('product_favorites')
        .insert({
          product_id: productId,
          user_id: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      throw error;
    }
  }

  // -------------------- JOB FAVORITES --------------------
  static async addJobToFavorites(jobId: string, userId: string): Promise<{ id: string; job_id: string; user_id: string; created_at: string; }> {
    try {
      const { data, error } = await supabase
        .from('job_favorites')
        .insert({
          job_id: jobId,
          user_id: userId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as any;
    } catch (error) {
      console.error('Error adding job to favorites:', error);
      throw error;
    }
  }

  static async removeJobFromFavorites(jobId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('job_favorites')
        .delete()
        .eq('job_id', jobId)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing job from favorites:', error);
      throw error;
    }
  }

  // Get all job favorites for a user
  static async getUserJobFavorites(userId: string): Promise<{ id: string; job_id: string; user_id: string; created_at: string; }[]> {
    try {
      const { data, error } = await supabase
        .from('job_favorites')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as any) || [];
    } catch (error) {
      console.error('Error fetching user job favorites:', error);
      throw error;
    }
  }

  // Get all job favorites for a user with full job and company details
  static async getUserJobFavoritesWithDetails(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('job_favorites')
        .select(`
          *,
          job:company_jobs(
            *,
            company:companies(*)
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as any) || [];
    } catch (error) {
      console.error('Error fetching user job favorites with details:', error);
      throw error;
    }
  }

  static async isJobFavorited(jobId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('job_favorites')
        .select('id')
        .eq('job_id', jobId)
        .eq('user_id', userId)
        .single();

      if (error && (error as any).code !== 'PGRST116') {
        throw error;
      }
      return !!data;
    } catch (error) {
      console.error('Error checking if job is favorited:', error);
      return false;
    }
  }

  static async toggleJobFavorite(jobId: string, userId: string): Promise<boolean> {
    try {
      const isFavorited = await this.isJobFavorited(jobId, userId);
      if (isFavorited) {
        await this.removeJobFromFavorites(jobId, userId);
        return false;
      } else {
        await this.addJobToFavorites(jobId, userId);
        return true;
      }
    } catch (error) {
      console.error('Error toggling job favorite:', error);
      // Re-throw with more detail if available
      const err: any = error as any;
      throw new Error(err?.message || err?.error_description || err?.details || err?.hint || 'Unknown error');
    }
  }

  static async getJobFavoriteStatuses(jobIds: string[], userId: string): Promise<Record<string, boolean>> {
    try {
      const { data, error } = await supabase
        .from('job_favorites')
        .select('job_id')
        .eq('user_id', userId)
        .in('job_id', jobIds);

      if (error) throw error;

      const favoriteStatuses: Record<string, boolean> = {};
      jobIds.forEach(id => {
        favoriteStatuses[id] = false;
      });

      data?.forEach((favorite: any) => {
        favoriteStatuses[favorite.job_id] = true;
      });

      return favoriteStatuses;
    } catch (error) {
      console.error('Error fetching job favorite statuses:', error);
      const favoriteStatuses: Record<string, boolean> = {};
      jobIds.forEach(id => {
        favoriteStatuses[id] = false;
      });
      return favoriteStatuses;
    }
  }

  // Remove a product from favorites
  static async removeFromFavorites(productId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('product_favorites')
        .delete()
        .eq('product_id', productId)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      throw error;
    }
  }

  // Get all favorites for a user
  static async getUserFavorites(userId: string): Promise<Favorite[]> {
    try {
      const { data, error } = await supabase
        .from('product_favorites')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user favorites:', error);
      throw error;
    }
  }

  // Check if a product is favorited by a user
  static async isProductFavorited(productId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('product_favorites')
        .select('id')
        .eq('product_id', productId)
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" error
        throw error;
      }
      
      return !!data;
    } catch (error) {
      console.error('Error checking if product is favorited:', error);
      return false;
    }
  }

  // Toggle favorite status (add if not favorited, remove if favorited)
  static async toggleFavorite(productId: string, userId: string): Promise<boolean> {
    try {
      const isFavorited = await this.isProductFavorited(productId, userId);
      
      if (isFavorited) {
        await this.removeFromFavorites(productId, userId);
        return false; // Now not favorited
      } else {
        await this.addToFavorites(productId, userId);
        return true; // Now favorited
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  }

  // Get favorite status for multiple products at once
  static async getFavoriteStatuses(productIds: string[], userId: string): Promise<Record<string, boolean>> {
    try {
      const { data, error } = await supabase
        .from('product_favorites')
        .select('product_id')
        .eq('user_id', userId)
        .in('product_id', productIds);

      if (error) throw error;

      const favoriteStatuses: Record<string, boolean> = {};
      productIds.forEach(id => {
        favoriteStatuses[id] = false;
      });

      data?.forEach(favorite => {
        favoriteStatuses[favorite.product_id] = true;
      });

      return favoriteStatuses;
    } catch (error) {
      console.error('Error fetching favorite statuses:', error);
      // Return all false if error occurs
      const favoriteStatuses: Record<string, boolean> = {};
      productIds.forEach(id => {
        favoriteStatuses[id] = false;
      });
      return favoriteStatuses;
    }
  }
}
